// ============================================
// ConverseTech Frontend - Transcript Panel
// ============================================

import { useEffect, useRef, useMemo } from 'react';
import type { TranscriptUpdate } from '@conversetech/shared';

interface TranscriptPanelProps {
  entries: TranscriptUpdate[];
}

interface AggregatedEntry {
  speaker: 'user' | 'ai';
  text: string;
  timestamp: number;
}

/**
 * Aggregate consecutive transcript entries from the same speaker.
 * This handles the streaming nature of Gemini transcription where
 * text comes in small chunks.
 */
function aggregateEntries(entries: TranscriptUpdate[]): AggregatedEntry[] {
  if (entries.length === 0) return [];

  const aggregated: AggregatedEntry[] = [];
  let current: AggregatedEntry | null = null;

  for (const entry of entries) {
    if (current && current.speaker === entry.speaker) {
      // Same speaker - append text with space if needed
      const separator = current.text.endsWith(' ') || entry.text.startsWith(' ') ? '' : ' ';
      current.text = current.text + separator + entry.text;
    } else {
      // Different speaker - start new entry
      if (current) {
        aggregated.push(current);
      }
      current = {
        speaker: entry.speaker,
        text: entry.text,
        timestamp: entry.timestamp,
      };
    }
  }

  if (current) {
    aggregated.push(current);
  }

  return aggregated;
}

export default function TranscriptPanel({ entries }: TranscriptPanelProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Aggregate consecutive entries from the same speaker
  const aggregatedEntries = useMemo(() => aggregateEntries(entries), [entries]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aggregatedEntries]);

  return (
    <div data-testid="transcript-panel" className="card bg-gray-800 p-4 h-[300px] sm:h-[400px] overflow-y-auto">
      <h3 className="text-xs sm:text-sm font-medium text-gray-400 mb-3 sm:mb-4">Live Transcript</h3>

      {aggregatedEntries.length === 0 ? (
        <div className="text-center text-gray-500 py-8 text-sm">
          Start speaking to see your transcript here...
        </div>
      ) : (
        <div className="space-y-2 sm:space-y-3">
          {aggregatedEntries.map((entry, index) => (
            <div
              key={index}
              data-testid={entry.speaker === 'user' ? 'transcript-user' : 'transcript-ai'}
              className={`p-2 sm:p-3 rounded-lg ${
                entry.speaker === 'user'
                  ? 'bg-primary-900/30 text-primary-100 ml-4 sm:ml-8'
                  : 'transcript-ai bg-gray-700 text-gray-100 mr-4 sm:mr-8'
              }`}
            >
              <div className="text-[10px] sm:text-xs text-gray-400 mb-1">
                {entry.speaker === 'user' ? 'You' : 'AI Coach'}
              </div>
              <div className="text-xs sm:text-sm">{entry.text}</div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      )}
    </div>
  );
}
